import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type FeatureStatus = 'implemented' | 'placeholder'
export type FeatureCategory = 'ui' | 'api' | 'store' | 'hook' | 'util' | 'config' | 'server' | 'other'

export interface FeatureEntry {
  id: string
  name: string
  description: string
  status: FeatureStatus
  category: FeatureCategory
  files: string[]
  timestamp: number
  sessionId: string
}

export interface Session {
  id: string
  startedAt: number
  endedAt?: number
  features: FeatureEntry[]
}

interface BuildLogState {
  sessions: Session[]
  currentSessionId: string | null

  startSession: () => string
  endSession: () => void
  logFeature: (feature: Omit<FeatureEntry, 'id' | 'timestamp' | 'sessionId'>) => string
  updateFeatureStatus: (featureId: string, status: FeatureStatus) => void
  removeFeature: (featureId: string) => void
  getCurrentSession: () => Session | null
  getSessionFeatures: (sessionId?: string) => FeatureEntry[]
  getFeaturesByStatus: (status: FeatureStatus) => FeatureEntry[]
  getFeaturesByCategory: (category: FeatureCategory) => FeatureEntry[]
  exportSessionLog: (sessionId?: string) => string
}

const generateId = () => crypto.randomUUID().slice(0, 8)

export const useBuildLogStore = create<BuildLogState>()(
  persist(
    (set, get) => ({
      sessions: [],
      currentSessionId: null,

      startSession: () => {
        const sessionId = generateId()
        const session: Session = {
          id: sessionId,
          startedAt: Date.now(),
          features: []
        }
        set(state => ({
          sessions: [...state.sessions, session],
          currentSessionId: sessionId
        }))
        return sessionId
      },

      endSession: () => {
        set(state => ({
          sessions: state.sessions.map(s =>
            s.id === state.currentSessionId
              ? { ...s, endedAt: Date.now() }
              : s
          ),
          currentSessionId: null
        }))
      },

      logFeature: (feature) => {
        const state = get()
        let sessionId = state.currentSessionId

        if (!sessionId) {
          sessionId = get().startSession()
        }

        const featureEntry: FeatureEntry = {
          ...feature,
          id: generateId(),
          timestamp: Date.now(),
          sessionId
        }

        set(state => ({
          sessions: state.sessions.map(s =>
            s.id === sessionId
              ? { ...s, features: [...s.features, featureEntry] }
              : s
          )
        }))

        return featureEntry.id
      },

      updateFeatureStatus: (featureId, status) => {
        set(state => ({
          sessions: state.sessions.map(s => ({
            ...s,
            features: s.features.map(f =>
              f.id === featureId ? { ...f, status } : f
            )
          }))
        }))
      },

      removeFeature: (featureId) => {
        set(state => ({
          sessions: state.sessions.map(s => ({
            ...s,
            features: s.features.filter(f => f.id !== featureId)
          }))
        }))
      },

      getCurrentSession: () => {
        const state = get()
        return state.sessions.find(s => s.id === state.currentSessionId) || null
      },

      getSessionFeatures: (sessionId) => {
        const state = get()
        const id = sessionId || state.currentSessionId
        const session = state.sessions.find(s => s.id === id)
        return session?.features || []
      },

      getFeaturesByStatus: (status) => {
        const session = get().getCurrentSession()
        return session?.features.filter(f => f.status === status) || []
      },

      getFeaturesByCategory: (category) => {
        const session = get().getCurrentSession()
        return session?.features.filter(f => f.category === category) || []
      },

      exportSessionLog: (sessionId) => {
        const state = get()
        const id = sessionId || state.currentSessionId
        const session = state.sessions.find(s => s.id === id)

        if (!session) return ''

        const implemented = session.features.filter(f => f.status === 'implemented')
        const placeholders = session.features.filter(f => f.status === 'placeholder')

        const lines = [
          `# Build Session Log`,
          `Session ID: ${session.id}`,
          `Started: ${new Date(session.startedAt).toISOString()}`,
          session.endedAt ? `Ended: ${new Date(session.endedAt).toISOString()}` : 'Status: Active',
          ``,
          `## Fully Implemented (${implemented.length})`,
          ...implemented.map(f => `- [x] **${f.name}** (${f.category}) - ${f.description}`),
          ``,
          `## Placeholders (${placeholders.length})`,
          ...placeholders.map(f => `- [ ] **${f.name}** (${f.category}) - ${f.description}`),
        ]

        return lines.join('\n')
      }
    }),
    {
      name: 'controldeck-build-log',
      partialize: state => ({
        sessions: state.sessions,
        currentSessionId: state.currentSessionId
      })
    }
  )
)
